import { useState, useEffect, useMemo } from 'react';
import client from '../../api/client';
import betClient from '../../api/betClient';
import useOdds from '../../hooks/useOdds';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import UpgradeModal from '../upgrade/UpgradeModal';

function renderMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={j} className="font-semibold text-[#111827] dark:text-[#e2e8f0]">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={j}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

const SPORTS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' },
  { key: 'boxing_boxing', label: 'Boxing' },
];

const MARKETS = [
  { key: 'spreads', label: 'Spread' },
  { key: 'h2h', label: 'ML' },
  { key: 'totals', label: 'Total' },
];

const ML_ONLY_SPORTS = ['mma_mixed_martial_arts', 'boxing_boxing'];

function fmtOdds(n) {
  return n > 0 ? `+${n}` : `${n}`;
}

// ── Game Selector ────────────────────────────────────────────────────────

function GameSelector({ sport, onAdd, legIds }) {
  const { events, loading } = useOdds(sport);
  const isMLOnly = ML_ONLY_SPORTS.includes(sport);
  const markets = isMLOnly ? MARKETS.filter((m) => m.key === 'h2h') : MARKETS;

  if (loading) {
    return <div className="space-y-1">{[...Array(3)].map((_, i) => <div key={i} className="h-10 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>;
  }

  if (!events.length) {
    return <p className="text-[#6B7280] dark:text-[#8892a8] text-[14px] py-4" style={{ fontFamily: 'var(--font-sans)' }}>No upcoming games for this sport.</p>;
  }

  // Group events by date label (Today, Tomorrow, or weekday)
  const grouped = [];
  let lastLabel = null;
  const now = new Date();
  const todayStr = now.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const tom = new Date(now); tom.setDate(tom.getDate() + 1);
  const tomStr = tom.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  for (const ev of events) {
    const d = new Date(ev.commence_time);
    const ds = d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const label = ds === todayStr ? 'Today' : ds === tomStr ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    if (label !== lastLabel) {
      grouped.push({ type: 'header', label });
      lastLabel = label;
    }
    grouped.push({ type: 'event', ev });
  }

  return (
    <div className="space-y-1.5">
      {grouped.map((item, i) =>
        item.type === 'header' ? (
          <div key={`hdr-${i}`} className={`flex items-center gap-2 ${i > 0 ? 'mt-3' : ''} mb-1`}>
            <span className="text-[11px] uppercase tracking-[0.5px] font-bold text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>{item.label}</span>
            <div className="flex-1 h-px bg-[#E5E7EB] dark:bg-[#1e2536]" />
          </div>
        ) : (
          <GameRow key={item.ev.id} event={item.ev} markets={markets} onAdd={onAdd} added={legIds.has(item.ev.id)} sport={sport} />
        )
      )}
    </div>
  );
}

function GameRow({ event, markets, onAdd, added, sport }) {
  const [expanded, setExpanded] = useState(false);
  const time = new Date(event.commence_time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150">
        <div>
          <span className="text-[14px] font-semibold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{event.away_team} @ {event.home_team}</span>
          <span className="text-[14px] text-[#6B7280] dark:text-[#8892a8] ml-2" style={{ fontFamily: 'var(--font-sans)' }}>{time}</span>
        </div>
        <div className="flex items-center gap-2">
          {added && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#E8F0FE] dark:bg-[rgba(0,91,179,0.15)] text-[#005BB3] dark:text-[#3b82f6]">ADDED</span>}
          <svg className={`w-3 h-3 text-[#6B7280] dark:text-[#8892a8] transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-3 py-2 border-t border-[#E5E7EB] dark:border-[#1e2536] space-y-2">
          {markets.map((mkt) => {
            const bookmaker = event.bookmakers?.[0];
            const market = bookmaker?.markets?.find((m) => m.key === mkt.key);
            if (!market) return null;

            return (
              <div key={mkt.key}>
                <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{mkt.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {market.outcomes.map((outcome) => {
                    const label = mkt.key === 'totals'
                      ? `${outcome.name} ${outcome.point}`
                      : mkt.key === 'spreads'
                      ? `${outcome.name} ${outcome.point > 0 ? '+' : ''}${outcome.point}`
                      : outcome.name;

                    return (
                      <button key={outcome.name} onClick={() => onAdd({
                        event_id: event.id,
                        sport_key: sport,
                        home_team: event.home_team,
                        away_team: event.away_team,
                        market: mkt.key,
                        selection: outcome.name,
                        odds: outcome.price,
                        point: outcome.point || null,
                        _label: label,
                        _matchup: `${event.away_team} @ ${event.home_team}`,
                      })}
                        className="px-3 py-1.5 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] hover:border-[#005BB3] hover:bg-[#E8F0FE] dark:hover:bg-[rgba(0,91,179,0.1)] transition-colors duration-150"
                      >
                        <span className="text-[13px] text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{label}</span>
                        <span className="text-[13px] text-[#6B7280] dark:text-[#8892a8] ml-1.5" style={{ fontFamily: 'var(--font-mono)' }}>{fmtOdds(outcome.price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Parlay Card ──────────────────────────────────────────────────────────

function ParlayCard({ legs, onRemove, parlayOdds, wager, setWager }) {
  const marketLabels = { spreads: 'Spread', h2h: 'ML', totals: 'Total' };

  return (
    <div className="rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB] dark:border-[#1e2536]">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
          <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Parlay Slip</h3>
        </div>
        <span className="text-[14px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{legs.length} leg{legs.length !== 1 ? 's' : ''}</span>
      </div>

      {legs.length === 0 ? (
        <div className="text-center py-6 px-3">
          <p className="text-[#9CA3AF] dark:text-[#505a6e] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>Add legs from the games above.</p>
          <p className="text-[#9CA3AF] dark:text-[#505a6e] text-[12px] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>Minimum 2 legs to build a parlay.</p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#1e2536]">
            {legs.map((leg, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#111827] dark:text-[#e2e8f0] truncate" style={{ fontFamily: 'var(--font-sans)' }}>{leg._matchup}</p>
                  <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#E8F0FE] dark:bg-[rgba(0,91,179,0.15)] text-[#005BB3] dark:text-[#3b82f6] mr-1">{marketLabels[leg.market] || leg.market}</span>
                    {leg._label}
                    <span className="ml-1" style={{ fontFamily: 'var(--font-mono)' }}>{fmtOdds(leg.odds)}</span>
                  </p>
                </div>
                <button onClick={() => onRemove(i)} className="text-[#9CA3AF] hover:text-[#DC2626] transition-colors duration-150 ml-2 p-0.5">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>

          {/* Wager + Payout */}
          <div className="px-3 py-3 border-t border-[#E5E7EB] dark:border-[#1e2536] space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Wager</span>
              <div className="flex items-center">
                <span className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mr-0.5">$</span>
                <input type="number" value={wager} onChange={(e) => setWager(Number(e.target.value) || 0)} min="1"
                  className="w-20 px-2 py-1 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#0f1219] text-[14px] text-[#111827] dark:text-[#e2e8f0] focus:outline-none focus:border-[#005BB3]"
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              </div>
            </div>

            {parlayOdds && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Combined Odds</p>
                  <p className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>
                    {fmtOdds(parlayOdds.combined_american)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Potential Payout</p>
                  <p className="text-[22px] font-semibold text-[#029456]" style={{ fontFamily: 'var(--font-mono)' }}>
                    {parlayOdds.potential_payout != null ? parlayOdds.potential_payout.toFixed(2) : '—'}
                  </p>
                  <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>
                    profit ${parlayOdds.potential_profit != null ? parlayOdds.potential_profit.toFixed(2) : '—'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── AI Analysis ──────────────────────────────────────────────────────────

function ParlayAnalysis({ legs, wager }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const tier = useEffectiveTier();
  const [modalOpen, setModalOpen] = useState(false);
  const locked = tier === 'free';

  function analyze() {
    setLoading(true); setError(null); setResult(null);
    betClient.post('/api/parlay/analyze', { legs, wager })
      .then((r) => setResult(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  const canAnalyze = legs.length >= 2;

  return (
    <div className="rounded-md border-l-[3px] border-l-[#005BB3] dark:border-l-[#3b82f6] border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0f1219] px-5 py-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>AI Parlay Analysis</h3>
        {result && !loading && !locked && (
          <button onClick={analyze} className="text-[12px] text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>Regenerate</button>
        )}
      </div>

      {!result && !loading && !error && (
        <>
          <p className="text-[#6B7280] dark:text-[#8892a8] text-[14px] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
            {canAnalyze
              ? 'Get AI-powered analysis of your parlay with per-leg confidence and historical data.'
              : 'Add at least 2 legs to your parlay to unlock AI analysis.'}
          </p>
          {locked ? (
            <button onClick={() => setModalOpen(true)}
              className="px-3 py-1.5 rounded-md bg-[#94a3b8] text-white text-[14px] font-medium cursor-pointer flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
              Analyze Parlay
              <span className="text-[9px] font-bold px-1 py-px rounded bg-white/20 uppercase">Pro</span>
            </button>
          ) : (
            <button onClick={analyze} disabled={!canAnalyze}
              className="px-3 py-1.5 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
              Analyze Parlay
            </button>
          )}
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-3">
          <svg className="animate-spin h-3.5 w-3.5 text-[#005BB3]" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-[#6B7280] dark:text-[#8892a8] text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>Analyzing {legs.length} legs...</span>
        </div>
      )}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] px-3 py-2 mt-1">
          <p className="text-[#DC2626] text-[13px]">Failed to analyze.</p>
          <button onClick={analyze} className="text-[12px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {result && !loading && (
        <div>
          {result.leg_stats?.some((ls) => ls.historical) && (
            <div className="mb-3">
              <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] mb-1.5" style={{ fontFamily: 'var(--font-sans)' }}>Historical Data</p>
              <div className="space-y-1">
                {result.leg_stats.map((ls, i) => ls.historical && (
                  <div key={i} className="flex items-center justify-between text-[13px] px-2 py-1 rounded-md bg-white dark:bg-[#151a25] border border-[#E5E7EB] dark:border-[#1e2536]">
                    <span className="text-[#111827] dark:text-[#e2e8f0] truncate mr-2" style={{ fontFamily: 'var(--font-sans)' }}>{ls.selection}</span>
                    <span className="text-[#6B7280] whitespace-nowrap" style={{ fontFamily: 'var(--font-mono)' }}>
                      {ls.historical.type}: {ls.historical.record} ({(ls.historical.pct * 100).toFixed(0)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.analysis?.content?.split('\n\n').map((para, i) => (
            <p key={i} className="text-[#1F2937] dark:text-[#8892a8] text-[14px] leading-relaxed mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{renderMarkdown(para)}</p>
          ))}

          {result.analysis?.created_at && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#E5E7EB] dark:border-[#1e2536]">
              <span className="text-[12px] text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{new Date(result.analysis.created_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} suggestedTier="pro" triggerFeature="Parlay Analysis" />
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────

export default function ParlayBuilder() {
  const [sport, setSport] = useState('americanfootball_nfl');
  const [legs, setLegs] = useState([]);
  const [wager, setWager] = useState(100);
  const [parlayOdds, setParlayOdds] = useState(null);

  const legIds = useMemo(() => new Set(legs.map((l) => l.event_id)), [legs]);

  function addLeg(leg) {
    if (legs.length >= 10) return;
    setLegs((prev) => {
      const filtered = prev.filter((l) => l.event_id !== leg.event_id);
      return [...filtered, leg];
    });
  }

  function removeLeg(index) {
    setLegs((prev) => prev.filter((_, i) => i !== index));
  }

  useEffect(() => {
    if (legs.length === 0) {
      setParlayOdds(null);
      return;
    }
    const oddsStr = legs.map((l) => l.odds).join(',');
    client.get(`/api/parlay/odds-calculator?odds=${oddsStr}&wager=${wager}`)
      .then((r) => setParlayOdds(r.data))
      .catch(() => setParlayOdds(null));
  }, [legs, wager]);

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Parlay Builder</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Build Your Parlay</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Combine up to 10 bets and get AI-powered analysis with historical data.</p>

      {/* Sport selector */}
      <div className="flex items-end gap-0 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
        <span className="text-[11px] uppercase tracking-[1px] font-semibold text-[#9CA3AF] dark:text-[#505a6e] mr-3 pb-2 select-none" style={{ fontFamily: 'var(--font-sans)' }}>Sport</span>
        {SPORTS.map((s) => (
          <button key={s.key} onClick={() => setSport(s.key)}
            className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
              sport === s.key
                ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
            }`}
            style={{ fontFamily: 'var(--font-sans)' }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Two-column layout on desktop */}
      <div className="max-w-[1100px]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          {/* Games */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
              <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Select Games</h3>
            </div>
            <GameSelector sport={sport} onAdd={addLeg} legIds={legIds} />
          </div>

          {/* Slip + Analysis */}
          <div className="space-y-4 lg:sticky lg:top-16 lg:self-start">
            <ParlayCard legs={legs} onRemove={removeLeg} parlayOdds={parlayOdds} wager={wager} setWager={setWager} />
            <ParlayAnalysis legs={legs} wager={wager} />
          </div>
        </div>
      </div>
    </div>
  );
}
